/**
 * PWA install-prompt store.
 *
 * Browsers fire `beforeinstallprompt` once per page load when the
 * SPA satisfies install criteria (manifest valid + SW active +
 * engagement heuristic). The event is captured at boot in
 * `src/main.ts` and stashed here so a UI surface (currently
 * `PortalShell` only) can call `prompt()` from a user gesture.
 *
 * `dismissed` persists in `localStorage` so a partner who taps
 * "Più tardi" doesn't see the banner again on the next visit.
 * The value is read once at store creation; subsequent reloads
 * pick it up via the same path.
 *
 * Note: per the W3C spec we MUST keep the captured event around
 * and call its `prompt()` from a user gesture handler. Storing
 * it in a ref is fine — the runtime guarantees the same event
 * remains valid until we call `prompt()`.
 */
import { ref, shallowRef } from 'vue'
import { defineStore, acceptHMRUpdate } from 'pinia'

const DISMISSED_KEY = 'fz.install.dismissed'

export const useInstallStore = defineStore('install', () => {
  // shallowRef so the captured event keeps its original identity —
  // the runtime guarantees the same `BeforeInstallPromptEvent`
  // instance must be passed back to `prompt()`. A regular `ref`
  // would wrap the event in a reactive proxy and break the spec
  // contract.
  const deferredPrompt = shallowRef<BeforeInstallPromptEvent | null>(null)
  const dismissed = ref(readDismissed())

  function captureEvent(event: BeforeInstallPromptEvent): void {
    deferredPrompt.value = event
  }

  async function promptInstall(): Promise<void> {
    const event = deferredPrompt.value
    if (!event) return
    // Spec: prompt() may only be called once per captured event.
    // Clear the ref before awaiting so a double-click can't fire it
    // again from the same captured object.
    deferredPrompt.value = null
    await event.prompt()
  }

  function dismiss(): void {
    dismissed.value = true
    try {
      localStorage.setItem(DISMISSED_KEY, '1')
    } catch {
      // localStorage is gated in some contexts (Safari private,
      // iframe sandbox). The dismissal still holds for the
      // current session via the in-memory ref.
    }
  }

  return { deferredPrompt, dismissed, captureEvent, promptInstall, dismiss }
})

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useInstallStore, import.meta.hot))
}
